import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import notifications from "../../common/Components/Notifications/Notification";
import { canUseRounds, isKarkhanaSupervisor } from "../../helpers/karkhanaEnabled";
import { getAttendance, getRounds, savePunch, setPersonDayRate, switchWork } from "../../services/karkhanaApi";
import "../gi-registry/gi-registry.scss";
import { fmtDay, rupees, shiftDay, todayIso } from "./rates/rateModel";
import { dropFromQueue, enqueue, flushQueue, newRequestId, QueuedEntry, readQueue } from "./rounds/entryQueue";
import PunchSheet, { ForLoom } from "./punches/PunchSheet";
import ArrivalBoard from "./punches/ArrivalBoard";
import { nowHHMM, OUT_REASONS, stateOf } from "./punches/punchModel";
import { AttendanceRow, buildAttendanceRows, countAttendance, quickInBody, SELF_NOTE } from "./punches/attendanceRows";
import AttendanceList from "./punches/AttendanceList";
import AttendanceTable from "./punches/AttendanceTable";
import { useWidthTier } from "./daily/useWidthTier";
import { dayLabel } from "./daily/dailyModel";
import "./daily/karkhana-daily.scss";
import AttendanceTabs from "./punches/AttendanceTabs";
import QuickAddPerson from "./people/QuickAddPerson";
import FindOrAddPerson from "./people/FindOrAddPerson";
import type { Person } from "./rounds/PersonPicker";
import { confirmDialog } from "./ui/dialog";
import { readRoster, refreshRoster } from "./punches/roster";
import "./workers/karkhana-workers.scss";
import "./rounds/karkhana-rounds.scss";
import "./punches/karkhana-punches.scss";
import TimeField, { formatTime12 } from "./ui/TimeField";
import { LangToggle, useKT } from "./i18n/kt";
import PageHeader from "./ui/PageHeader";
import { cameFromInApp } from "./ui/PageHeader";
import SearchField from "./ui/SearchField";
import { withoutRetired } from "./sarees/sareeEditModel";
import { kp } from "./karkhanaPaths";
import { useAddressParam, withParams } from "./ui/addressState";

const readAuth = () => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null")?.users || {};
  } catch {
    return {};
  }
};

/** Send a queued punch or switch; the queue stores which one it was. */
const sendQueued = (b: Record<string, unknown>) => {
  const { kind, ...body } = b as any;
  return kind === "switch" ? switchWork(body) : savePunch(body);
};

/** Who is in, who is out, and their hours and time pay, for one day (K07, K16, K18). */
const KarkhanaAttendancePage = () => {
  const { t, lang } = useKT();
  const history = useHistory();
  const location = useLocation();
  const auth = readAuth();
  const role = auth?.role;
  const orgId: string = auth?.organizationResponse?._id || "org";
  const queueKey = `${orgId}:attendance`;
  const [date, setDate] = useState(() => new URLSearchParams(location.search).get("date") || todayIso());
  const [data, setData] = useState<any>(null);
  const [looms, setLooms] = useState<ForLoom[]>([]);
  // The name search is in the address (replaced while typing), so Back from a person keeps it.
  const [q, setQ] = useAddressParam("q");
  // A person's day (?person=…) and the arrivals board (?mode=arrive) are pages: Back returns here.
  const params = new URLSearchParams(location.search);
  const openId = params.get("person");
  const arrival = params.get("mode") === "arrive";
  const searchFor = (d: string, extra: { person?: string; mode?: string } = {}) =>
    withParams(history.location.search, { date: d !== todayIso() ? d : null, person: extra.person, mode: extra.mode });
  const setOpenId = (id: string | null) => (id ? history.push({ search: searchFor(date, { person: id }) }) : backToList());
  const setArrival = (on: boolean) => (on ? history.push({ search: searchFor(date, { mode: "arrive" }) }) : backToList());
  const backToList = () => {
    if (cameFromInApp(location.key)) history.goBack();
    else history.replace({ search: searchFor(date) });
  };
  const [queue, setQueue] = useState<QueuedEntry[]>(() => readQueue(queueKey));
  const [offline, setOffline] = useState(false);
  const [roster, setRoster] = useState(() => readRoster(orgId));
  // A new person walks in: added and marked in from here.
  const [adding, setAdding] = useState(false);
  const [markIn, setMarkIn] = useState(true);
  const [dayRate, setDayRate] = useState("");
  const [outBusy, setOutBusy] = useState(false);
  const owner = role === "ORGANIZATION_ADMIN";
  const tier = useWidthTier();
  // One-tap In / Out: who is being saved, who is choosing an Out reason, who was just added.
  const [busyId, setBusyId] = useState<string | null>(null);
  // In or Out tapped on the list: the time shows first, to confirm as it is or change (came at 8, tapped at 10).
  const [ask, setAsk] = useState<{ row: AttendanceRow; type: "IN" | "OUT" } | null>(null);
  const [askTime, setAskTime] = useState("");
  const [added, setAdded] = useState<{ id: string; code: string; name: string; at?: string; noRate: boolean } | null>(null);
  const [addKey, setAddKey] = useState(0);
  // "+ New person" searches Workers first; the form opens only for someone who isn't there.
  const [finding, setFinding] = useState(false);
  const [addName, setAddName] = useState("");

  const load = useCallback(async (d: string) => {
    try {
      const res: any = await getAttendance(d);
      if (res?.success) {
        setData(res.data);
        setOffline(false);
      } else {
        notifications({ title: t("Error"), message: t(res?.message || "Could not load attendance"), type: "danger" });
      }
    } catch {
      setOffline(true);
    }
  }, []);

  const flush = useCallback(async () => {
    if (!readQueue(queueKey).length) return;
    const r = await flushQueue(queueKey, sendQueued);
    setQueue(readQueue(queueKey));
    setOffline(r.offline);
    if (r.sent) {
      notifications({ title: t("Sent"), message: t("{{n}} saved punches sent", { n: r.sent }), type: "success" });
      load(date);
    }
  }, [queueKey, date, load]);

  useEffect(() => {
    if (!canUseRounds(role)) {
      history.replace(kp.home());
      return;
    }
    refreshRoster(orgId).then(setRoster).catch(() => undefined);
  }, [role, history, orgId]);

  useEffect(() => {
    load(date);
    const cur = history.location;
    history.replace({ ...cur, search: withParams(cur.search, { date: date !== todayIso() ? date : null }) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, load, history]);

  // Looms and the saree on each that day, so time work can be tagged (warping, dyeing).
  useEffect(() => {
    getRounds(date)
      .then((res: any) =>
        setLooms(
          res?.success
            ? (withoutRetired(res.data)?.looms || []).map((l: any) => ({ id: l.id, code: l.code, sareeNumber: l.assignment?.sareeNumber || null }))
            : []
        )
      )
      .catch(() => setLooms([]));
  }, [date]);

  useEffect(() => {
    flush();
    const onOnline = () => flush();
    window.addEventListener("online", onOnline);
    const t = window.setInterval(flush, 30000);
    return () => {
      window.removeEventListener("online", onOnline);
      window.clearInterval(t);
    };
  }, [flush]);

  const people: any[] = useMemo(() => data?.people || [], [data]);
  // A supervisor's own worker rows: another supervisor or the admin marks them.
  const selfIds = useMemo(() => new Set<string>(data?.selfPersonIds || []), [data]);
  const rows = useMemo(() => buildAttendanceRows(data), [data]);
  const shown = rows.filter((r) => {
    const term = q.trim().toUpperCase();
    return !term || String(r.code).toUpperCase().includes(term) || String(r.name || "").toUpperCase().includes(term);
  });
  // A person just added sits at the top, highlighted, until the next one.
  const ordered = added ? [...shown.filter((r) => r.personId === added.id), ...shown.filter((r) => r.personId !== added.id)] : shown;
  const open = people.find((p) => p.personId === openId) || null;
  const counts = countAttendance(rows);
  const queuedFor = new Set(queue.filter((x) => (x.body as any).date === date).map((x) => (x.body as any).personId));

  const onSaved = async ({ queued, keepOpen }: { queued?: boolean; keepOpen?: boolean }) => {
    setQueue(readQueue(queueKey));
    if (!queued) await load(date);
    if (!keepOpen) backToList();
  };

  if (!canUseRounds(role)) return null;
  const isToday = date === todayIso();

  /** The evening: everyone still in goes home at the same time. */
  const everyoneOut = async () => {
    const inNow = people.filter((p) => stateOf(p) === "IN" && !selfIds.has(p.personId));
    if (!inNow.length) return;
    const when = nowHHMM();
    if (!(await confirmDialog({ title: t("Mark {{n}} people out at {{time}} (home)?", { n: inNow.length, time: when }) }))) return;
    setOutBusy(true);
    let ok = 0;
    for (const p of inNow) {
      try {
        const res: any = await savePunch({ personId: p.personId, date, time: when, type: "OUT", reason: "HOME", clientRequestId: newRequestId() });
        if (res?.success) ok++;
      } catch {
        // Counted below; they can be marked one by one.
      }
    }
    setOutBusy(false);
    notifications({
      title: ok === inNow.length ? t("Done") : t("Some not marked"),
      message: t("{{n}} of {{total}} marked out", { n: ok, total: inNow.length }),
      type: ok === inNow.length ? "success" : "warning",
    });
    load(date);
  };

  if (arrival) {
    return (
      <div className="gi-registry-page kw-page kr-rounds">
        <PageHeader title={`${t("Mark arrivals")} · ${fmtDay(date)}`} back={`${kp.attendance()}${searchFor(date)}`} backLabel={t("Back")}>
          <LangToggle />
        </PageHeader>
        {data?.late ? <div className="kr-banner is-warn">{t("This day is 3 or more days back. Entries wait for the admin's approval.")}</div> : null}
        <ArrivalBoard
          date={date}
          isToday={isToday}
          people={people}
          roster={roster.filter((p) => !selfIds.has(p.id))}
          queueKey={queueKey}
          onMarked={() => {
            setQueue(readQueue(queueKey));
            load(date);
          }}
        />
      </div>
    );
  }

  if (open) {
    return (
      <div className="gi-registry-page kw-page kr-rounds kr-rounds--loom">
        <PageHeader title={`${t("Attendance")} · ${fmtDay(date)}`} back={`${kp.attendance()}${searchFor(date)}`} backLabel={t("Back")}>
          <LangToggle />
        </PageHeader>
        <PunchSheet
          key={open.personId + date}
          asPage
          person={open}
          date={date}
          isToday={isToday}
          late={!!data?.late}
          activities={data?.activities || []}
          queueKey={queueKey}
          roster={roster}
          looms={looms}
          self={selfIds.has(open.personId)}
          onClose={backToList}
          onSaved={onSaved}
        />
      </div>
    );
  }

  /** One tap: In now. Without signal it is kept on the phone and sent later. */
  const punch = async (body: Record<string, unknown>, who: AttendanceRow) => {
    setBusyId(who.personId);
    try {
      const res: any = await savePunch(body);
      if (!res?.success) notifications({ title: t("Not saved"), message: t(res?.message || "Something went wrong"), type: "danger" });
      else if (res.data?.duplicate) notifications({ title: t("Already marked"), message: t("{{code}} was already marked by someone else", { code: who.code }), type: "info" });
    } catch {
      enqueue(queueKey, { clientRequestId: String(body.clientRequestId), body: { ...body, kind: "punch" }, loomCode: who.code, queuedAt: new Date().toISOString() });
      setQueue(readQueue(queueKey));
    } finally {
      setBusyId(null);
      load(date);
    }
  };
  const askPunch = (row: AttendanceRow, type: "IN" | "OUT") => {
    setFinding(false);
    setAskTime(nowHHMM());
    setAsk({ row, type });
  };
  const confirmPunch = (reason?: string) => {
    if (!ask) return;
    if (!askTime) {
      notifications({ title: t("Enter the time"), message: t("When did this happen?"), type: "warning" });
      return;
    }
    const { row, type } = ask;
    setAsk(null);
    if (type === "IN") punch(quickInBody(row, date, askTime, newRequestId()), row);
    else punch({ personId: row.personId, date, time: askTime, type: "OUT", reason, clientRequestId: newRequestId() }, row);
  };
  const openRow = (r: AttendanceRow) => {
    if (r.kind === "NOT_YET") {
      // Their day is empty; the arrivals board marks them in at another time.
      setArrival(true);
      return;
    }
    setOpenId(r.personId);
  };

  /** From the search: mark someone in now (a regular goes back to their last work). */
  const inFromSearch = (p: Person, row?: AttendanceRow) =>
    selfIds.has(p.id)
      ? notifications({ title: t("Not marked"), message: t(SELF_NOTE), type: "info" })
      : askPunch(row || { personId: p.id, code: p.code, name: p.name, kind: "NOT_YET", work: "", minutes: 0, noRate: false, waiting: false }, "IN");
  const openFromSearch = (p: Person) => {
    const r = rows.find((x) => x.personId === p.id);
    if (!r || r.kind === "NOT_YET") return;
    setFinding(false);
    setOpenId(p.id);
  };
  const startAdding = (name: string) => {
    setFinding(false);
    setAddName(name);
    setAddKey((k) => k + 1);
    setAdding(true);
  };
  const finder = (docked: boolean) => (
    <FindOrAddPerson
      roster={roster}
      rows={rows}
      isToday={isToday}
      docked={docked}
      busyId={busyId}
      onIn={inFromSearch}
      onOpen={openFromSearch}
      onAdd={startAdding}
      onClose={() => setFinding(false)}
    />
  );

  const addForm = (docked: boolean) => (
    <QuickAddPerson
      key={addKey}
      initialName={addName}
      docked={docked}
      role="HELPER"
      title={docked ? `+ ${t("New person")}` : t("New person")}
      submitLabel={isToday && markIn ? t("Add & mark in") : t("Add")}
      onCancel={() => setAdding(false)}
      extra={
        <>
          {owner ? (
            <>
              <label className="kq__label" htmlFor="kq-dayrate">
                {t("Pay per day")} <i>{t("optional · can be set later")}</i>
              </label>
              <input id="kq-dayrate" className="kq__input" value={dayRate} onChange={(e) => setDayRate(e.target.value.replace(/[^\d.]/g, ""))} inputMode="decimal" placeholder="₹ 450" />
            </>
          ) : null}
          {isToday ? (
            <div className="kq__chips">
              <button type="button" className={`kr-choice${markIn ? " is-on" : ""}`} aria-pressed={markIn} onClick={() => setMarkIn(!markIn)}>
                {markIn ? "✓ " : ""}
                {t("Mark in now · {{time}}", { time: nowHHMM() })}
              </button>
            </div>
          ) : null}
        </>
      }
      onDone={async (p) => {
        let rated = false;
        if (owner && dayRate.trim()) {
          const r: any = await setPersonDayRate(p.id, dayRate.trim(), date).catch(() => null);
          rated = !!r?.success;
          if (!rated) notifications({ title: t("Day rate not saved"), message: t("Set it later in Pay"), type: "warning" });
        }
        let at: string | undefined;
        if (isToday && markIn) {
          at = nowHHMM();
          const r: any = await savePunch({ personId: p.id, date, time: at, type: "IN", clientRequestId: newRequestId() }).catch(() => null);
          if (!r?.success) {
            at = undefined;
            notifications({ title: t("Not marked in"), message: t("Mark {{name}} in from the list", { name: p.name }), type: "warning" });
          }
        }
        setAdded({ id: p.id, code: p.code, name: p.name, at, noRate: !rated });
        setAdding(false);
        setDayRate("");
        setAddKey((k) => k + 1);
        await refreshRoster(orgId).then(setRoster).catch(() => undefined);
        load(date);
      }}
    />
  );

  const isWide = tier !== "list";
  // Until the day loads the counts aren't known yet: "…", not 0.
  const n = (v: number) => (data ? v : "…");
  const subline = [
    dayLabel(date, lang),
    t("{{n}} came", { n: n(counts.came) }),
    ...(isToday ? [t("{{n}} in now", { n: n(counts.inNow) }), t("{{n}} not yet", { n: n(counts.notYet) })] : []),
  ].join(" · ");

  const banners = (
    <>
      {offline ? <div className="kr-banner is-warn">{t("No connection. You can keep marking people; it is sent when the signal is back.")}</div> : null}
      {queue.length ? (
        <div className="kr-banner is-info">
          {t("{{n}} saved on this phone and not sent yet.", { n: queue.length })}{" "}
          <button type="button" className="btn btn-link btn-sm p-0" onClick={flush}>
            {t("Send now")}
          </button>
          {queue.filter((x) => x.error).map((x) => (
            <div key={x.clientRequestId} className="mt-1">
              <strong>{x.loomCode}</strong> {(x.body as any).time}: {t(x.error || "")}{" "}
              <button
                type="button"
                className="btn btn-link btn-sm p-0"
                onClick={() => {
                  dropFromQueue(queueKey, x.clientRequestId);
                  setQueue(readQueue(queueKey));
                }}
              >
                {t("Discard")}
              </button>
            </div>
          ))}
        </div>
      ) : null}
      {data?.late ? <div className="kr-banner is-warn">{t("This day is 3 or more days back. Entries wait for the admin's approval.")}</div> : null}
      {added ? (
        <div className="kat-added" role="status">
          ✓ {added.at ? t("{{name}} added ({{code}}) and marked in at {{time}}", { name: added.name, code: added.code, time: added.at }) : t("{{name}} added ({{code}})", { name: added.name, code: added.code })}
          <button type="button" aria-label={t("Close")} onClick={() => setAdded(null)}>
            ×
          </button>
        </div>
      ) : null}
    </>
  );

  const listProps = { isToday, busyId, queued: queuedFor, newId: added?.id || null, onOpen: openRow, onIn: (r: AttendanceRow) => askPunch(r, "IN"), onOut: (r: AttendanceRow) => askPunch(r, "OUT") };
  const empty =
    data && !rows.length ? (
      <div className="kw-empty">{t("Nobody is marked in for {{day}} yet. Tap + New person, or Mark several in for people already in Workers.", { day: isToday ? t("today") : fmtDay(date) })}</div>
    ) : null;
  const everyoneOutBtn =
    isToday && counts.inNow ? (
      <button type="button" className="kd-btn kd-btn--line kat-everyone" disabled={outBusy} onClick={everyoneOut}>
        {outBusy ? t("Saving…") : t("Everyone out · home")}
      </button>
    ) : null;
  const dayNav = (
    <div className="kd-day kat-day">
      <button type="button" className="kd-iconbtn" aria-label={t("Previous day")} onClick={() => setDate(shiftDay(date, -1))}>
        ‹
      </button>
      <label className="kd-day__pick">
        <span>{isToday ? `${t("Today")} · ${dayLabel(date, lang)}` : dayLabel(date, lang)}</span>
        <input
          type="date"
          value={date}
          max={todayIso()}
          onChange={(e) => e.target.value && setDate(e.target.value)}
          onClick={(e) => (e.currentTarget as any).showPicker?.()}
          aria-label={t("Date")}
        />
      </label>
      <button type="button" className="kd-iconbtn" aria-label={t("Next day")} disabled={isToday} onClick={() => setDate(shiftDay(date, 1))}>
        ›
      </button>
    </div>
  );
  const search =
    rows.length > 12 ? <SearchField value={q} onChange={setQ} placeholder={t("Find someone, e.g. J12")} label={t("Find someone")} autoCapitalize="characters" /> : null;
  // Always reachable: on a past day with nobody listed this is the only way to mark someone in.
  const severalLink = data && (!isToday || counts.notYet > 1 || !rows.length) ? (
    <button type="button" className="btn btn-link btn-sm p-0 kat-several" onClick={() => setArrival(true)}>
      {t("Mark several in at once")}
    </button>
  ) : null;

  return (
    <div className={`gi-registry-page kw-page kr-rounds kat-page is-${tier}${!isWide && everyoneOutBtn ? " has-foot" : ""}`}>
      <PageHeader
        title={
          <>
            {t("Attendance")}
            {isWide ? <small>{subline}</small> : null}
          </>
        }
      >
        {isWide ? everyoneOutBtn : null}
        <LangToggle />
      </PageHeader>
      <AttendanceTabs active="hours" hours={n(counts.came)} />
      {dayNav}
      {banners}

      {!data ? <div className="kw-empty">{t("Loading…")}</div> : null}

      {data && !isWide ? (
        <>
          <div className="kat-tiles">
            <div>
              <strong>{counts.came}</strong>
              <span>{t("came")}</span>
            </div>
            {isToday ? (
              <>
                <div>
                  <strong>{counts.inNow}</strong>
                  <span>{t("in now")}</span>
                </div>
                <div>
                  <strong>{counts.notYet}</strong>
                  <span>{t("not yet")}</span>
                </div>
              </>
            ) : (
              <div>
                <strong>{rupees(data.timePayTotal, 0)}</strong>
                <span>{t("Time pay")}</span>
              </div>
            )}
          </div>
          <button type="button" className="kd-btn kd-btn--primary kat-add" onClick={() => setFinding(true)}>
            + {t("New person")}
          </button>
          {search}
          {empty || <AttendanceList rows={ordered} {...listProps} />}
          {severalLink}
          {everyoneOutBtn ? <div className="kf-foot">{everyoneOutBtn}</div> : null}
        </>
      ) : null}

      {data && isWide ? (
        <div className="kat-split">
          <div className="kat-split__main">
            {search}
            {empty || (tier === "table" ? <AttendanceTable rows={ordered} {...listProps} /> : <AttendanceList rows={ordered} {...listProps} />)}
            {severalLink}
          </div>
          <aside className="kat-split__side">
            {adding ? (
              <>
                <button type="button" className="btn btn-link btn-sm p-0 kfa-back" onClick={() => setAdding(false)}>
                  ‹ {t("Back to search")}
                </button>
                {addForm(true)}
              </>
            ) : (
              finder(true)
            )}
          </aside>
        </div>
      ) : null}

      {finding && !isWide ? finder(false) : null}
      {adding && !isWide ? addForm(false) : null}

      {ask ? (
        <>
          <div className="kd-backdrop" onClick={() => setAsk(null)} />
          <section className="kd-actions" role="dialog" aria-modal="true" aria-label={ask.type === "IN" ? t("In") : t("Out")}>
            <div className="kd-actions__grab" aria-hidden="true" />
            <header className="kd-actions__head">
              <h3>
                {ask.row.code} {ask.row.name} · {ask.type === "IN" ? t("In") : t("Out")}
              </h3>
              <span>{ask.type === "IN" ? t("Came in at this time? Change it if not.") : t("Went out at this time? Change it if not.")}</span>
            </header>
            <div className="kat-asktime">
              <TimeField value={askTime} onChange={setAskTime} label={t("Time")} />
              <button type="button" className="kr-choice" onClick={() => setAskTime(nowHHMM())}>
                {t("Now")}
              </button>
            </div>
            {ask.type === "IN" ? (
              <button type="button" className="kd-btn kat-askok" onClick={() => confirmPunch()}>
                {t("Mark in at {{time}}", { time: formatTime12(askTime) })}
              </button>
            ) : (
              <>
                <span className="kr-label">{t("Why are they going out?")}</span>
                <div className="kd-chips">
                  {OUT_REASONS.map((x) => (
                    <button key={x.key} type="button" className="kr-choice" onClick={() => confirmPunch(x.key)}>
                      {t(x.label)}
                    </button>
                  ))}
                </div>
              </>
            )}
            <button type="button" className="kd-btn kd-btn--line kd-actions__cancel" onClick={() => setAsk(null)}>
              {t("Cancel")}
            </button>
          </section>
        </>
      ) : null}
    </div>
  );
};

export default KarkhanaAttendancePage;
